// ============================================================================
// LA PAGE À IMPRIMER — un document HTML autoportant, confié au navigateur.
//
// UTILITÉ : le back-office est full web (décision 2026-08-26). Imprimer, c'est donc produire une
// page dont chaque mesure est en MILLIMÈTRES et laisser le navigateur la rendre — A4 comme rouleau.
//
// ⚠️ FONCTION PURE, ET C'EST DÉLIBÉRÉ : elle rend une CHAÎNE. C'est ce qui permet de vérifier par des
// tests qu'une planche entamée décale bien, qu'un code-barres porte sa consigne d'encre et qu'un
// rouleau coupe entre deux étiquettes — trois choses qu'aucune relecture n'attrape et qui, en vrai,
// se découvrent sur du papier gâché.
//
// ⚠️ AUCUN CALCUL DE DISPOSITION ICI. Largeur d'un élément et tracé des barres viennent de
// `@orgazone/core-catalog` : l'aperçu de l'éditeur et cette page doivent montrer LA MÊME étiquette,
// sans quoi c'est celle qu'on n'a pas regardée qui part à l'imprimante.
// ============================================================================
import { ean13Runs, elementWidthMm, FIELD_BARCODE, type LabelTemplate } from '@orgazone/core-catalog';
import { t } from '../i18n';

/** Une étiquette à tirer : ses valeurs, et en combien d'exemplaires. */
export interface PrintRow {
  values: Partial<Record<string, string>>;
  copies: number;
}

/** Ce qui distingue une impression d'une autre. */
export interface PrintOptions {
  /**
   * Étiquettes DÉJÀ CONSOMMÉES sur la première planche (A4 seulement).
   * ⚠️ CE N'EST PAS UN CONFORT : une planche Avery entamée coûte le prix d'une planche neuve. Sans
   * ce décalage, reprendre une feuille à moitié pleine imprime sur le vide et gâche les étiquettes
   * restantes — l'erreur ne se voit qu'une fois la feuille sortie.
   */
  usedOnFirstSheet?: number;
}

/** Échappement HTML — une désignation peut contenir n'importe quoi, y compris des chevrons. */
function esc(v: unknown): string {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Un nombre en CSS — point décimal, jamais la virgule française qui casserait la règle. */
const css = (v: number) => String(Math.round(v * 100) / 100);

/**
 * Les barres d'un EAN-13, en dégradé CSS.
 * ⚠️ `print-color-adjust: exact` N'EST PAS DÉCORATIF : sans lui le navigateur « économise l'encre »,
 * les barres sortent délavées, et le code-barres devient illisible au scanner. C'est la seule ligne
 * de cette page dont l'absence produit un défaut invisible à l'écran.
 */
function barcodeElement(code: string, xMm: number, yMm: number, widthMm: number, heightMm: number): string {
  const runs = ean13Runs(code);
  // ⚠️ SANS CODE VALIDE, ON N'IMPRIME RIEN À CET ENDROIT. Une trame décorative ferait un faux
  //    code-barres sur un article réel — quelqu'un le scannerait.
  if (!runs) return '';
  const stops = runs
    .map((r) => `${r.bar ? '#000' : 'transparent'} ${css(r.from * 100)}% ${css(r.to * 100)}%`)
    .join(',');
  return `<i style="position:absolute;display:block;left:${css(xMm)}mm;top:${css(yMm)}mm;`
    + `width:${css(widthMm)}mm;height:${css(heightMm)}mm;`
    + `background-image:linear-gradient(90deg,${stops});background-size:100% 100%;background-repeat:no-repeat;`
    + '-webkit-print-color-adjust:exact;print-color-adjust:exact"></i>';
}

/** UNE étiquette, éléments compris. */
function labelHtml(template: LabelTemplate, values: Partial<Record<string, string>>): string {
  let inner = '';
  for (const el of template.elements) {
    const width = elementWidthMm(el);
    if (el.field === FIELD_BARCODE) {
      inner += barcodeElement(values.barcode ?? '', el.xMm, el.yMm, width, el.sizeMm);
      continue;
    }
    inner += `<i style="position:absolute;left:${css(el.xMm)}mm;top:${css(el.yMm)}mm;`
      + (width ? `width:${css(width)}mm;overflow:hidden;` : '')
      + `font-size:${css(el.sizeMm)}mm;font-weight:${el.bold ? 700 : 400};text-align:${el.align};`
      + 'font-style:normal;line-height:1.05;white-space:nowrap;text-overflow:ellipsis">'
      + `${esc(values[el.field])}</i>`;
  }
  // ⚠️ `break-inside: avoid` : sans lui une étiquette peut être coupée en deux par un saut de page.
  const roll = template.kind === 'roll' ? 'page-break-after:always;' : '';
  return `<div style="position:relative;width:${css(template.widthMm)}mm;height:${css(template.heightMm)}mm;`
    + `overflow:hidden;break-inside:avoid;${roll}">${inner}</div>`;
}

/** Une case vide de planche — c'est elle qui saute les étiquettes déjà consommées. */
function blankCell(template: LabelTemplate): string {
  return `<div style="width:${css(template.widthMm)}mm;height:${css(template.heightMm)}mm"></div>`;
}

/** Combien d'étiquettes au total — le chiffre que l'écran annonce avant de lancer. */
export function countLabels(rows: readonly PrintRow[]): number {
  return rows.reduce((n, r) => n + Math.max(0, Math.trunc(r.copies)), 0);
}

/**
 * Combien de FEUILLES une impression consomme — A4 seulement.
 * ⚠️ LES CASES SAUTÉES COMPTENT : reprendre une planche à moitié pleine ne rend pas la feuille
 * gratuite, et annoncer « 2 feuilles » pour une impression qui en mange 3 fait manquer du papier.
 */
export function countSheets(template: LabelTemplate, labels: number, usedOnFirstSheet = 0): number {
  if (template.kind !== 'a4') return 0;
  const perSheet = Math.max(1, (template.cols ?? 1) * (template.rows ?? 1));
  const skipped = Math.max(0, Math.min(perSheet - 1, Math.trunc(usedOnFirstSheet)));
  return Math.ceil((labels + skipped) / perSheet);
}

/**
 * LE DOCUMENT COMPLET, prêt à imprimer.
 *
 * ⚠️ `@page` PORTE LE FORMAT RÉEL : `size: 40mm 25mm; margin: 0` pour un rouleau, `size: A4;
 * margin: 5mm` pour une planche. C'est ce qui fait qu'une thermique installée en imprimante système
 * sort une étiquette par étiquette, sans marge parasite.
 * ⚠️ CE QUE CETTE PAGE NE PEUT PAS EMPÊCHER : un pilote réglé sur « ajuster à la page ». Il
 * anéantirait tout le travail au millimètre — et le code-barres avec. Ça se DIT à l'écran ; le seul
 * remède est humain.
 */
export function buildPrintDocument(template: LabelTemplate, rows: readonly PrintRow[], opts: PrintOptions = {}): string {
  const page = template.kind === 'a4'
    ? '@page{size:A4;margin:5mm}'
    : `@page{size:${css(template.widthMm)}mm ${css(template.heightMm)}mm;margin:0}`;
  const wrap = template.kind === 'a4'
    ? `display:grid;grid-template-columns:repeat(${template.cols ?? 1},${css(template.widthMm)}mm);gap:0`
    : 'display:block';

  let body = '';
  if (template.kind === 'a4') {
    const perSheet = Math.max(1, (template.cols ?? 1) * (template.rows ?? 1));
    const skipped = Math.max(0, Math.min(perSheet - 1, Math.trunc(opts.usedOnFirstSheet ?? 0)));
    body += blankCell(template).repeat(skipped);
  }
  for (const r of rows) {
    const copies = Math.max(0, Math.trunc(r.copies));
    for (let i = 0; i < copies; i++) body += labelHtml(template, r.values);
  }

  // ⚠️ LE TITRE DU DOCUMENT SE TRADUIT : il s'affiche dans la fenêtre d'impression du navigateur
  //    et sur l'en-tête de page. Le reste du gabarit est du BALISAGE, et n'a rien à y faire.
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${t('Étiquettes')}</title><style>`
    + page
    + 'html,body{margin:0;padding:0;font-family:Inter,Helvetica,Arial,sans-serif;color:#000}'
    + `.sheet{${wrap}}`
    + '</style></head><body><div class="sheet">'
    + body
    // ⚠️ LA BALISE FERMANTE EST COUPÉE EN DEUX : écrite d'un bloc, elle refermerait prématurément
    //    tout script qui embarquerait ce module en ligne — et la page d'impression sortirait tronquée.
    + '</div><script>window.onload=function(){window.focus();window.print();};<' + '/script></body></html>';
}

/** Ce que l'ouverture d'une page d'impression a réellement donné. */
export type PrintOutcome = { ok: true; via: 'window' | 'iframe' } | { ok: false; reason: string };

/**
 * OUVRE LA PAGE ET DEMANDE L'IMPRESSION.
 *
 * ⚠️ REPLI SUR UN CADRE CACHÉ QUAND LA FENÊTRE EST BLOQUÉE : les bloqueurs de fenêtres surgissantes
 * sont la règle, pas l'exception, et un « Lancer l'impression » qui ne fait rien se lit comme une
 * application cassée.
 * ⚠️ ET SI LE REPLI ÉCHOUE AUSSI, ON LE DIT (§23.0). Rendre `true` d'office ferait afficher
 * « impression lancée » alors que rien n'est parti — l'opérateur attendrait devant une imprimante
 * silencieuse en croyant que le papier arrive.
 */
export function openPrintDocument(html: string, host: Document = document): PrintOutcome {
  try {
    const w = host.defaultView?.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
      return { ok: true, via: 'window' };
    }
  } catch {
    // La fenêtre a été refusée : on tente le cadre caché ci-dessous, qui n'a pas besoin d'autorisation.
  }
  try {
    const frame = host.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    host.body.appendChild(frame);
    const doc = frame.contentDocument;
    // ⚠️ CE MESSAGE EST VU PAR LE MAGASIN : il remonte par le motif d'échec, et l'écran des étiquettes
    //    l'affiche (« L'impression n'a pas pu s'ouvrir : … »). Une erreur ATTRAPÉE et MONTRÉE se
    //    traduit — c'est l'invariant JAMAIS attrapé qui reste en dur.
    if (!doc) throw new Error(t('le cadre d’impression n’a pas de document'));
    doc.open();
    doc.write(html);
    doc.close();
    // ⚠️ CE DÉLAI N'EST PAS UNE SIMULATION DE TRAVAIL (§23.0) : il laisse au navigateur le temps de
    //    poser la mise en page avant d'ouvrir sa boîte d'impression. Sans lui, on imprime une page
    //    vide. Le second retire le cadre une fois la boîte forcément fermée.
    setTimeout(() => { try { frame.contentWindow?.focus(); frame.contentWindow?.print(); } catch { /* la boîte d'impression a été refusée par le navigateur */ } }, 300);
    setTimeout(() => { try { frame.remove(); } catch { /* déjà retiré */ } }, 60_000);
    return { ok: true, via: 'iframe' };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
}
